import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { useAuth } from '../contexts/AuthContext';

const ForgotPassword = () => {
  const { requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsSubmitting(true);

    try {
      const result = await requestPasswordReset(email);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Une erreur est survenue');
      }
    } catch (err) {
      setError('Une erreur est survenue lors de la demande de réinitialisation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 32px 0 rgba(0,0,0,0.08)',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}
              >
                Mot de passe oublié
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}
              >
                Entrez votre adresse email pour recevoir un lien de réinitialisation de mot de passe
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
                  Un email de réinitialisation a été envoyé à votre adresse email. Veuillez vérifier
                  votre boîte de réception.
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Adresse email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'grey.400' },
                      '&:hover fieldset': { borderColor: 'grey.700' },
                      '&.Mui-focused fieldset': { borderColor: 'grey.700' },
                    },
                    '& .MuiInputLabel-root': { color: 'grey.600' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'grey.800' },
                    '& .MuiInputLabel-root.MuiFormLabel-filled': { color: 'grey.800' },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    borderRadius: '50px',
                    py: 1.5,
                    fontSize: '1.1rem',
                    boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                    '&:hover': {
                      boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                    },
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Envoyer le lien de réinitialisation'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => navigate('/login')}
                    sx={{ p: 0, minWidth: 'auto', ml: 0.5, boxShadow: 'none' }}
                  >
                    Retour à la connexion
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default ForgotPassword;
